import api from '../../../services/api';
import { store } from '../../../redux/store'; // Import the named export 'store' instead of default export
import {
    setSettings,
    setSellerAccounts,
    setLoading,
    setError,
    addSellerAccountSuccess,
    updateSellerAccountSuccess,
    deleteSellerAccountSuccess,
    setWorkers,
    updateWorkerStatusSuccess,
    setQueueStatus,
    setJobs,
    resetJobs,
    setUsers,
    addUserSuccess,
    updateUserSuccess,
    deleteUserSuccess,
    setRoles,
    addRoleSuccess,
    updateRoleSuccess,
    deleteRoleSuccess,
    setPermissionMatrix
} from '../reducers';

// Fetch user settings
export const fetchSettings = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // In a real app, this would be an API call
        // const response = await api.get('/settings');
        
        // For now, we'll use localStorage
        const settings = JSON.parse(localStorage.getItem('userSettings')) || {
            language: 'vi',
            theme: 'light'
        };
        
        dispatch(setSettings(settings));
        return settings;
    } catch (error) {
        dispatch(setError('Không thể tải cài đặt người dùng'));
        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

// Update user settings
export const updateSettings = (settings) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // In a real app, this would be an API call
        // const response = await api.put('/settings', settings);
        
        // For now, we'll use localStorage
        localStorage.setItem('userSettings', JSON.stringify(settings));
        
        // Apply theme immediately
        if (settings.theme) {
            document.documentElement.setAttribute('data-theme', settings.theme);
        }
        
        // Apply language immediately
        if (settings.language) {
            document.documentElement.setAttribute('lang', settings.language);
        }
        
        dispatch(setSettings(settings));
        return { success: true };
    } catch (error) {
        dispatch(setError('Không thể cập nhật cài đặt người dùng'));
        return { success: false, error: error.message };
    } finally {
        dispatch(setLoading(false));
    }
};

// Fetch seller accounts
export const fetchSellerAccounts = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/seller-accounts');
        
        // Extract the array of seller accounts from the response.data.data field
        const accounts = response.data?.data || [];
        
        dispatch(setSellerAccounts(accounts));
        return accounts;
    } catch (error) {
        console.error('Error fetching seller accounts:', error);
        
        // For demo purposes, return mock data if API doesn't exist yet
        const mockAccounts = [
            {
                id: '1',
                name: 'Amazon US Account',
                sellerId: 'A2OKDUCQXK8BCRP',
                email: 'seller@example.com',
                marketplaces: ['US', 'CA'],
                webhookUrl: 'https://api.example.com/webhooks/amazon',
                status: 'active'
            },
            {
                id: '2',
                name: 'Amazon UK Account',
                sellerId: 'A1B2C3D4E5F6G7H',
                email: 'uk-seller@example.com',
                marketplaces: ['UK', 'DE', 'FR'],
                status: 'active'
            }
        ];
        
        dispatch(setSellerAccounts(mockAccounts));
        return mockAccounts;
    } finally {
        dispatch(setLoading(false));
    }
};

// Add a new seller account
export const addSellerAccount = (accountData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/seller-accounts', accountData);
        
        // Extract the actual account data from the response
        const accountResult = response.data?.data || response.data;
        
        dispatch(addSellerAccountSuccess(accountResult));
        return { success: true, data: accountResult };
    } catch (error) {
        console.error('Error adding seller account:', error);
        
        // For demo purposes
        const mockAccount = {
            id: Date.now().toString(),
            ...accountData
        };
        
        dispatch(addSellerAccountSuccess(mockAccount));
        return { success: true, data: mockAccount };
    } finally {
        dispatch(setLoading(false));
    }
};

// Update a seller account
export const updateSellerAccount = (id, accountData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/seller-accounts/${id}`, accountData);
        
        // Extract the actual account data from the response
        const accountResult = response.data?.data || response.data;
        
        dispatch(updateSellerAccountSuccess({ id, ...accountResult }));
        return { success: true, data: accountResult };
    } catch (error) {
        console.error('Error updating seller account:', error);
        
        // For demo purposes
        const mockUpdatedAccount = {
            id,
            ...accountData
        };
        
        dispatch(updateSellerAccountSuccess(mockUpdatedAccount));
        return { success: true, data: mockUpdatedAccount };
    } finally {
        dispatch(setLoading(false));
    }
};

// Delete a seller account
export const deleteSellerAccount = (id) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        await api.delete(`/seller-accounts/${id}`);
        dispatch(deleteSellerAccountSuccess(id));
        return { success: true };
    } catch (error) {
        console.error('Error deleting seller account:', error);
        
        // For demo purposes
        dispatch(deleteSellerAccountSuccess(id));
        return { success: true };
    } finally {
        dispatch(setLoading(false));
    }
};

// Test seller webhook
export const testSellerWebhook = (webhookUrl) => async () => {
    try {
        const response = await api.post('/test-webhook', { url: webhookUrl });
        return {
            success: true,
            message: 'Webhook test thành công. Máy chủ đã nhận được request.'
        };
    } catch (error) {
        console.error('Error testing webhook:', error);
        
        // For demo purposes
        // Simulate a successful test
        return {
            success: Math.random() > 0.3, // 70% success rate for demo
            message: Math.random() > 0.3
                ? 'Webhook test thành công. Máy chủ đã nhận được request.'
                : 'Không thể kết nối đến URL webhook. Vui lòng kiểm tra lại URL và đảm bảo endpoint có thể truy cập được.'
        };
    }
};

// Fetch workers
export const fetchWorkers = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/workers');
        
        // Extract the workers data from the response
        const workersData = response.data?.data || [];
        
        dispatch(setWorkers(workersData));
        return workersData;
    } catch (error) {
        console.error('Error fetching workers:', error);
        dispatch(setError('Không thể tải danh sách worker'));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

// Check worker health
export const checkWorkerHealth = (workerId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get(`/workers/${workerId}/health`);
        
        // Extract the health data from the response
        const healthData = response.data?.data || response.data;
        
        dispatch(updateWorkerStatusSuccess({ 
            id: workerId,
            status: healthData.status,
            lastHealthyCheck: new Date()
        }));
        
        return { success: true, data: healthData };
    } catch (error) {
        console.error(`Error checking worker health (ID: ${workerId}):`, error);
        
        dispatch(updateWorkerStatusSuccess({ 
            id: workerId,
            status: 'error',
            lastHealthyCheck: new Date()
        }));
        
        return { 
            success: false,
            status: 'error',
            error: error.message
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Check health of all workers
export const checkAllWorkersHealth = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/workers/health/check-all');
        
        // Extract the health data from the response
        const healthData = response.data?.data || [];
        
        // Update each worker's status
        healthData.forEach(worker => {
            dispatch(updateWorkerStatusSuccess({ 
                id: worker.id,
                status: worker.status,
                lastHealthyCheck: new Date()
            }));
        });
        
        return { success: true, data: healthData };
    } catch (error) {
        console.error('Error checking all workers health:', error);
        dispatch(setError('Không thể kiểm tra trạng thái của các worker'));
        return { success: false, error: error.message };
    } finally {
        dispatch(setLoading(false));
    }
};

// Update SP API Credentials on a worker and link it to a seller account
export const updateWorkerSpApiCredentials = (sellerId, workerData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Call API to update worker credentials and link to seller account
        const response = await api.post('/seller-accounts/set-worker-credentials', {
            sellerId,
            ...workerData
        });
        
        // Extract the updated account data from the response
        const updatedAccount = response.data?.data || response.data;
        
        // Update the seller account in the redux store
        dispatch(updateSellerAccountSuccess(updatedAccount));
        
        return { success: true, data: updatedAccount };
    } catch (error) {
        console.error('Error updating worker SP API credentials:', error);
        return { 
            success: false, 
            error: error.message || 'Failed to update SP API credentials'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Update worker
export const updateWorker = (workerData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/workers/${workerData.id}`, workerData);
        
        // Extract the updated worker data from the response
        const updatedWorker = response.data?.data;
        
        if (updatedWorker) {
            dispatch(updateWorkerStatusSuccess({ 
                ...updatedWorker,
                lastHealthyCheck: new Date()
            }));
        }
        
        return { success: true, data: updatedWorker };
    } catch (error) {
        console.error('Error updating worker:', error);
        return { 
            success: false, 
            error: error.message || 'Failed to update worker'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Create SQS destination for a seller account
export const createSqsDestination = (destinationData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/seller-accounts/create-sqs-destination', destinationData);
        
        // Update the seller account in the redux store with the updated account data
        if (response.data?.data?.account) {
            dispatch(updateSellerAccountSuccess(response.data.data.account));
        }
        
        return { 
            success: true, 
            data: response.data?.data || response.data,
            message: response.data?.message || 'SQS destination created successfully'
        };
    } catch (error) {
        console.error('Error creating SQS destination:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to create SQS destination'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Delete SQS destination for a seller account
export const deleteSqsDestination = (deletionData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.delete('/seller-accounts/sqs-destination', {
            data: deletionData
        });
        
        // Update the seller account in the redux store
        if (response.data?.data?.id) {
            dispatch(updateSellerAccountSuccess(response.data.data));
        }
        
        return { 
            success: true, 
            data: response.data?.data || response.data,
            message: response.data?.message || 'SQS destination deleted successfully'
        };
    } catch (error) {
        console.error('Error deleting SQS destination:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to delete SQS destination'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Update subscriptions for an existing SQS destination
export const updateSqsSubscriptions = (subscriptionData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put('/seller-accounts/sqs-subscription', subscriptionData);
        
        // Extract the updated account data from the response
        if (response.data?.data?.account) {
            dispatch(updateSellerAccountSuccess(response.data.data.account));
        }
        
        return { 
            success: true, 
            data: response.data?.data || response.data,
            message: response.data?.message || 'SQS subscriptions updated successfully'
        };
    } catch (error) {
        console.error('Error updating SQS subscriptions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to update SQS subscriptions'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Check status of an SQS destination
export const checkSqsDestinationStatus = (sellerId, destinationId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get(`/seller-accounts/sqs-destination/status?sellerId=${sellerId}&destinationId=${destinationId}`);
        
        return { 
            success: true, 
            data: response.data?.data || response.data,
            message: response.data?.message || 'SQS destination status checked successfully'
        };
    } catch (error) {
        console.error('Error checking SQS destination status:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to check SQS destination status'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Check if an SQS destination exists for a seller account before opening modal
export const checkSqsDestinationExists = (sellerId) => async (dispatch) => {
    try {
        // First check if the account has a notificationDestination in local data
        const response = await api.get(`/seller-accounts/${sellerId}/check-sqs-destination`);
        
        return { 
            success: true,
            exists: response.data?.exists || false,
            data: response.data?.data || null,
            message: response.data?.message || 'SQS destination check completed'
        };
    } catch (error) {
        console.error('Error checking if SQS destination exists:', error);
        return { 
            success: false,
            exists: false,
            error: error.response?.data?.message || error.message || 'Failed to check if SQS destination exists'
        };
    }
};

// Queue Monitoring Actions - Only call Main Worker (Single Source of Truth)
export const fetchQueueStatus = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Only call Main Worker - it will aggregate data from all queues
        const response = await api.get('/main-worker/queue/status');

        if (response.data?.success) {
            const queueStatus = response.data.queues || {};
            dispatch(setQueueStatus(queueStatus));
            return { success: true, data: queueStatus };
        } else {
            throw new Error(response.data?.message || 'Failed to fetch queue status');
        }
    } catch (error) {
        console.error('Error fetching queue status:', error);
        dispatch(setError('Không thể tải trạng thái queue'));
        
        // Return empty data if API fails
        dispatch(setQueueStatus({}));
        return { success: false, error: error.message };
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchJobs = (queueType = 'all', status = 'all', start = 0, end = 49) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Use queueType directly - no complex mapping needed
        const backendQueueType = queueType;
        
        console.log('Fetching jobs - Frontend queueType:', queueType, 'Backend queueType:', backendQueueType, 'Status:', status);
        
        // Only call Main Worker with queue type parameter
        const response = await api.get(`/main-worker/queue/jobs/${status}`, {
            params: { 
                queueType: backendQueueType, 
                start, 
                end 
            }
        });
        
        if (response.data?.success) {
            const jobs = response.data.jobs || [];
            dispatch(setJobs(jobs));
            return { success: true, data: jobs };
        } else {
            throw new Error(response.data?.message || 'Failed to fetch jobs');
        }
    } catch (error) {
        console.error('Error fetching jobs:', error);
        dispatch(setError('Không thể tải danh sách jobs'));
        
        // Return empty array if API fails
        dispatch(setJobs([]));
        return { success: false, error: error.message };
    } finally {
        dispatch(setLoading(false));
    }
};

export const pauseQueue = (queueType) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Use queueType directly - no mapping needed
        console.log('Pausing queue - queueType:', queueType);
        
        const response = await api.post('/main-worker/queue/pause', { queueType });
        
        if (response.data?.success) {
            // Refresh queue status after pause
            dispatch(fetchQueueStatus());
            return { success: true, message: response.data.message };
        } else {
            throw new Error(response.data?.message || 'Failed to pause queue');
        }
    } catch (error) {
        console.error('Error pausing queue:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to pause queue'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const resumeQueue = (queueType) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Use queueType directly - no mapping needed
        console.log('Resuming queue - queueType:', queueType);
        
        const response = await api.post('/main-worker/queue/resume', { queueType });
        
        if (response.data?.success) {
            // Refresh queue status after resume
            dispatch(fetchQueueStatus());
            return { success: true, message: response.data.message };
        } else {
            throw new Error(response.data?.message || 'Failed to resume queue');
        }
    } catch (error) {
        console.error('Error resuming queue:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to resume queue'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const removeJob = (queueType, jobId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Use queueType directly - no mapping needed
        console.log('Removing job - queueType:', queueType, 'jobId:', jobId);
        
        const response = await api.delete(`/main-worker/queue/job/${jobId}`, {
            data: { queueType }
        });
        
        if (response.data?.success) {
            return { success: true, message: response.data.message, isRepeatable: response.data.isRepeatable, repeatKey: response.data.repeatKey };
        } else {
            throw new Error(response.data?.message || 'Failed to remove job');
        }
    } catch (error) {
        console.error('Error removing job:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to remove job'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const promoteJob = (queueType, jobId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        console.log('Promoting delayed job - queueType:', queueType, 'jobId:', jobId);

        const response = await api.post(`/main-worker/queue/job/${jobId}/promote`, { queueType });

        if (response.data?.success) {
            return { success: true, message: response.data.message, note: response.data.note };
        } else {
            throw new Error(response.data?.message || 'Failed to promote job');
        }
    } catch (error) {
        console.error('Error promoting job:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to promote job'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const retryJob = (queueType, jobId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        console.log('Retrying failed job - queueType:', queueType, 'jobId:', jobId);

        const response = await api.post(`/main-worker/queue/job/${jobId}/retry`, { queueType });

        if (response.data?.success) {
            return { success: true, message: response.data.message };
        } else {
            throw new Error(response.data?.message || 'Failed to retry job');
        }
    } catch (error) {
        console.error('Error retrying job:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to retry job'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const cleanQueue = (queueType, status = 'completed', grace = 0) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        // Use queueType directly - no mapping needed
        console.log('Cleaning queue - queueType:', queueType, 'status:', status);
        
        const response = await api.post('/main-worker/queue/clean', { 
            queueType, 
            status, 
            grace 
        });
        
        if (response.data?.success) {
            return { success: true, message: response.data.message };
        } else {
            throw new Error(response.data?.message || 'Failed to clean queue');
        }
    } catch (error) {
        console.error('Error cleaning queue:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to clean queue'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Reset jobs state
export const resetJobsState = () => (dispatch) => {
    dispatch(resetJobs());
};

// Trello Settings Actions
export const setTrelloWebhook = (webhookData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/trello/webhook', webhookData);
        
        if (response.data?.success) {
            return { 
                success: true, 
                data: response.data.data,
                message: response.data.message || 'Webhook Trello đã được thiết lập thành công'
            };
        } else {
            throw new Error(response.data?.message || 'Failed to set Trello webhook');
        }
    } catch (error) {
        console.error('Error setting Trello webhook:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to set Trello webhook'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const testTrelloWebhook = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/trello/webhook/test');
        
        if (response.data?.success) {
            return { 
                success: true, 
                data: response.data.data,
                message: response.data.message || 'Test webhook thành công'
            };
        } else {
            throw new Error(response.data?.message || 'Failed to test Trello webhook');
        }
    } catch (error) {
        console.error('Error testing Trello webhook:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to test Trello webhook'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const getTrelloWebhookStatus = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/trello/webhook/status');
        
        if (response.data?.success) {
            return { 
                success: true, 
                data: response.data.data,
                message: response.data.message || 'Webhook status retrieved successfully'
            };
        } else {
            throw new Error(response.data?.message || 'Failed to get Trello webhook status');
        }
    } catch (error) {
        console.error('Error getting Trello webhook status:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Failed to get Trello webhook status'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// User Management Actions
export const fetchUsers = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/users');
        
        // Extract the array of users from the response
        const users = response.data || [];
        
        dispatch(setUsers(users));
        return users;
    } catch (error) {
        console.error('Error fetching users:', error);
        dispatch(setError('Unable to load user list'));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const addUser = (userData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/users', userData);
        
        // Extract the actual user data from the response
        const userResult = response.data || response.data?.data;
        
        dispatch(addUserSuccess(userResult));
        return { success: true, data: userResult };
    } catch (error) {
        console.error('Error adding user:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể tạo người dùng mới'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const updateUser = (id, userData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/users/${id}`, userData);
        
        // Extract the actual user data from the response
        const userResult = response.data || response.data?.data;
        
        dispatch(updateUserSuccess({ id, ...userResult }));
        return { success: true, data: userResult };
    } catch (error) {
        console.error('Error updating user:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể cập nhật thông tin người dùng'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const deleteUser = (id) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        await api.delete(`/users/${id}`);
        
        dispatch(deleteUserSuccess(id));
        return { success: true };
    } catch (error) {
        console.error('Error deleting user:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể xóa người dùng'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const changeUserPassword = (id, passwordData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post(`/users/${id}/change-password`, passwordData);
        
        return { 
            success: true, 
            message: response.data?.message || 'Đổi mật khẩu thành công'
        };
    } catch (error) {
        console.error('Error changing user password:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể đổi mật khẩu'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const lockUser = (id) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/users/${id}`, { active: false });
        
        const userResult = response.data || response.data?.data;
        dispatch(updateUserSuccess({ id, ...userResult }));
        
        return { success: true };
    } catch (error) {
        console.error('Error locking user:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể khóa người dùng'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const unlockUser = (id) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/users/${id}`, { active: true });
        
        const userResult = response.data || response.data?.data;
        dispatch(updateUserSuccess({ id, ...userResult }));
        
        return { success: true };
    } catch (error) {
        console.error('Error unlocking user:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể mở khóa người dùng'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Role Management Actions
export const fetchRoles = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/roles');
        
        // Extract the array of roles from the response
        const roles = response.data || [];
        
        dispatch(setRoles(roles));
        return roles;
    } catch (error) {
        console.error('Error fetching roles:', error);
        dispatch(setError('Unable to load role list'));
        
        // Return fallback roles if API fails
        const fallbackRoles = [
            { id: 1, name: 'Super Admin', description: 'Full system administration rights', isActive: true },
            { id: 2, name: 'Admin', description: 'System administrator', isActive: true },
            { id: 3, name: 'Manager', description: 'Manager role', isActive: true },
            { id: 4, name: 'Staff', description: 'Staff member', isActive: true },
            { id: 5, name: 'User', description: 'Regular user', isActive: true }
        ];
        dispatch(setRoles(fallbackRoles));
        return fallbackRoles;
    } finally {
        dispatch(setLoading(false));
    }
};

export const addRole = (roleData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post('/roles', roleData);
        
        const roleResult = response.data || response.data?.data;
        
        dispatch(addRoleSuccess(roleResult));
        return { success: true, data: roleResult };
    } catch (error) {
        console.error('Error adding role:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể tạo vai trò mới'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const updateRole = (id, roleData) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.put(`/roles/${id}`, roleData);
        
        const roleResult = response.data || response.data?.data;
        
        dispatch(updateRoleSuccess({ id, ...roleResult }));
        return { success: true, data: roleResult };
    } catch (error) {
        console.error('Error updating role:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể cập nhật vai trò'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const deleteRole = (id) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        await api.delete(`/roles/${id}`);
        
        dispatch(deleteRoleSuccess(id));
        return { success: true };
    } catch (error) {
        console.error('Error deleting role:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể xóa vai trò'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchRolePermissions = (roleId) => async () => {
    try {
        const response = await api.get(`/roles/${roleId}/permissions`);
        return { success: true, data: response.data || [] };
    } catch (error) {
        console.error('Error fetching role permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể tải quyền của vai trò'
        };
    }
};

export const assignRolePermissions = (roleId, permissionIds) => async () => {
    try {
        const response = await api.post(`/roles/${roleId}/permissions`, { permissionIds });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error assigning role permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Không thể gán quyền cho vai trò'
        };
    }
};

export const assignUserRole = (userId, roleName) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.post(`/users/${userId}/assign-role`, { roleName });
        
        const userResult = response.data || response.data?.data;
        
        // Update the user in the store with the new role
        dispatch(updateUserSuccess({ id: userId, ...userResult }));
        return { success: true, data: userResult };
    } catch (error) {
        console.error('Error assigning user role:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to assign role to user'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

// Permission Matrix Actions
export const fetchPermissionMatrix = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await api.get('/roles/matrix');
        console.log('Permission Matrix Response:', response.data); // Debug log
        dispatch(setPermissionMatrix(response.data));
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching permission matrix:', error);
        dispatch(setError(error.response?.data?.message || error.message || 'Unable to load permission matrix'));
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to load permission matrix'
        };
    } finally {
        dispatch(setLoading(false));
    }
};

export const updateRolePermissions = (roleId, permissionIds) => async (dispatch) => {
    try {
        const response = await api.put(`/roles/matrix/${roleId}`, { permissionIds });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating role permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to update role permissions'
        };
    }
};

export const bulkUpdateRolePermissions = (rolePermissionUpdates) => async (dispatch) => {
    try {
        const response = await api.put('/roles/matrix/bulk-update', { rolePermissionUpdates });
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error bulk updating role permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to bulk update role permissions'
        };
    }
};

export const autoSeedPermissions = () => async (dispatch) => {
    try {
        const response = await api.post('/roles/matrix/auto-seed');
        return { 
            success: true, 
            data: response.data,
            message: response.data.message
        };
    } catch (error) {
        console.error('Error auto-seeding permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to auto-seed permissions'
        };
    }
};

export const copyRolePermissions = (sourceRoleId, targetRoleId) => async (dispatch) => {
    try {
        const response = await api.post('/roles/matrix/copy-permissions', { 
            sourceRoleId, 
            targetRoleId 
        });
        return { 
            success: true, 
            data: response.data,
            message: response.data.message
        };
    } catch (error) {
        console.error('Error copying role permissions:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to copy role permissions'
        };
    }
};

export const fetchPermissionsByResource = () => async (dispatch) => {
    try {
        const response = await api.get('/roles/permissions-by-resource');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error fetching permissions by resource:', error);
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unable to load permissions by resource'
        };
    }
};